import { useCallback, useRef } from 'react'

import {
  useAudioInputs,
  useDeviceLabelTriggerStatus,
  useLogger,
  useMeetingManager,
  useVideoInputs,
} from 'amazon-chime-sdk-component-library-react'
import { LogLevel, MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import { useNavigate } from 'react-router-dom'

import { useMeetingContext } from '../'
import { PATH } from '../../constants'
import { useDeviceIdProvider } from '../useDeviceIdProvider'

export const useMeeting = () => {
  const { getRoute } = useDeviceIdProvider()
  const meetingManager = useMeetingManager()
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const logger = useLogger()
  logger.setLogLevel(LogLevel.ERROR)
  const { meeting, attendee } = useMeetingContext()
  const { devices: audioDevices } = useAudioInputs()
  const { devices: videoDevices } = useVideoInputs()
  const deviceLabelTriggerStatus = useDeviceLabelTriggerStatus()

  const startMeeting = useCallback(async () => {
    if (!meeting || !attendee) {
      return
    }

    const configuration = new MeetingSessionConfiguration(meeting, attendee)
    await meetingManager.join(configuration)
    await meetingManager.start()
  }, [meetingManager, meeting, attendee])

  const leaveMeeting = useCallback(async () => {
    await meetingManager.leave()
    navigate(getRoute(PATH.finish))
  }, [meetingManager, navigate, getRoute])

  const shareScreen = useCallback(async () => {
    await meetingManager.audioVideo?.startContentShareFromScreenCapture()
  }, [meetingManager])

  const muteAudio = useCallback(() => {
    meetingManager.audioVideo?.realtimeMuteLocalAudio()
  }, [meetingManager])

  const unMuteAudio = useCallback(() => {
    meetingManager.audioVideo?.realtimeUnmuteLocalAudio()
  }, [meetingManager])

  const stopLocalVideo = useCallback(() => {
    meetingManager.audioVideo?.stopLocalVideoTile()
  }, [meetingManager])

  const selectDevice = useCallback(async () => {
    if (audioDevices.length > 0) {
      await meetingManager.startAudioInputDevice(audioDevices[0].deviceId)
    }
    if (videoDevices.length > 0) {
      await meetingManager.startVideoInputDevice(videoDevices[0].deviceId)
    }
  }, [meetingManager, audioDevices, videoDevices])

  return {
    videoRef,
    leaveMeeting,
    shareScreen,
    stopLocalVideo,
    muteAudio,
    unMuteAudio,
    startMeeting,
    selectDevice,
    deviceLabelTriggerStatus,
  }
}

export default useMeeting
